/**
 * The scrub state machine (WS-17b) driving `GameBoardPage`'s replay/history UI. Pure and
 * React-free on purpose — a `useReducer` slots this straight in, and it's unit-testable
 * without touching Yjs or the DOM.
 *
 * Two states only: `live` (the ordinary board — whatever `useGameMoves` currently derives,
 * which for a finished game is already its final position) and `scrubbing` (viewing a
 * specific replay frame, view-only, never writes anything). `scrubbing` covers both use
 * cases from the brief with the same machinery: a finished game's "Watch replay" playback,
 * and an active game's "see how it went" history scrub — the only difference is whether
 * `playing` auto-advances the frame.
 *
 * The one deliberately un-obvious rule: a new live move landing *while* scrubbing never
 * yanks the view back to live. It only flips `newMoveAvailable`, so the UI can show a quiet
 * "game moved on — back to live?" chip. Leaving scrub mode is always an explicit action
 * (`back-to-live`) the person takes themselves.
 */

export interface ScrubState {
  mode: 'live' | 'scrubbing';
  /** Index into a `ReplayFrame[]` (see `@awwyeah/shared`'s `replay.ts`). Only meaningful
   * while `mode === 'scrubbing'`. */
  frame: number;
  /** Auto-advancing playback (the finished-game "Watch replay" control). */
  playing: boolean;
  /** A live move landed while scrubbing — show the "back to live?" chip. Cleared the moment
   * scrubbing starts or ends. */
  newMoveAvailable: boolean;
}

export type ScrubAction =
  /** Enter scrub mode at a specific frame — "Watch replay" (frame 0) or "see how it went"
   * (typically the last frame, so the person scrubs backward from where they are). */
  | { type: 'start-scrub'; frame: number }
  | { type: 'scrub-to'; frame: number }
  | { type: 'step'; delta: number; maxFrame: number }
  | { type: 'play' }
  | { type: 'pause' }
  /** One playback tick (~0.8s cadence, driven by the component's own timer). */
  | { type: 'tick'; maxFrame: number }
  | { type: 'jump-to-end'; frame: number }
  | { type: 'back-to-live' }
  | { type: 'new-move-arrived' };

export const initialScrubState: ScrubState = {
  mode: 'live',
  frame: 0,
  playing: false,
  newMoveAvailable: false,
};

export function scrubReducer(state: ScrubState, action: ScrubAction): ScrubState {
  switch (action.type) {
    case 'start-scrub':
      return {
        mode: 'scrubbing',
        frame: Math.max(0, action.frame),
        playing: false,
        newMoveAvailable: false,
      };

    case 'scrub-to':
      if (state.mode !== 'scrubbing') return state;
      return { ...state, frame: Math.max(0, action.frame), playing: false };

    case 'step': {
      if (state.mode !== 'scrubbing') return state;
      const next = Math.max(0, Math.min(action.maxFrame, state.frame + action.delta));
      return { ...state, frame: next, playing: false };
    }

    case 'play':
      if (state.mode !== 'scrubbing') return state;
      return { ...state, playing: true };

    case 'pause':
      if (state.mode !== 'scrubbing') return state;
      return { ...state, playing: false };

    case 'tick': {
      if (state.mode !== 'scrubbing' || !state.playing) return state;
      const next = state.frame + 1;
      if (next >= action.maxFrame) return { ...state, frame: action.maxFrame, playing: false };
      return { ...state, frame: next };
    }

    case 'jump-to-end':
      if (state.mode !== 'scrubbing') return state;
      return { ...state, frame: Math.max(0, action.frame), playing: false };

    case 'back-to-live':
      return { ...initialScrubState };

    case 'new-move-arrived':
      // Only meaningful mid-scrub; a no-op the rest of the time (including redundantly
      // dispatched ones — see the component's simple "moves.length grew" trigger).
      if (state.mode !== 'scrubbing') return state;
      if (state.newMoveAvailable) return state;
      return { ...state, newMoveAvailable: true };

    default:
      return state;
  }
}
